import { notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UPDATE_PRICER } from "utils/api/graphql/mutations/pricers";
import { INSURANCE_PLAN_PRICING_AXES } from "utils/api/graphql/queries/insurance-plans";
import { PRICER, PRICERS } from "utils/api/graphql/queries/pricers";
import { DATE_FORMAT, SCREENS } from "utils/constants";
import CollectivePricerEditor from "../../Collective/Editor";
import { toPricerDateForm, toPricerDatePayload } from "../../RateLookup/form";

const CollectiveForm = ({ pricer, axes, saving, onSave, cancel }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const [name, setName] = useState(pricer.name || "");
  const [dates, setDates] = useState(toPricerDateForm(pricer));
  const [prices, setPrices] = useState(pricer.prices || {});

  return (
    <>
      <Header
        title={DETAIL.HEADER.TITLE}
        subtitle={get(pricer, "insurancePlan.name")}
        actions={[
          {
            text: DETAIL.HEADER.DISCARD,
            onClick: cancel,
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
          {
            text: DETAIL.HEADER.SAVE,
            onClick: () =>
              onSave({ name, ...toPricerDatePayload(dates), prices }),
            type: "primary",
            icon: <BiCheck size={20} />,
            loading: saving,
          },
        ]}
      />
      <Card title={FORM.GENERAL_INFO}>
        <div className="grid--2">
          <Input
            label={FORM.NAME}
            placeholder={FORM.NAME}
            value={name}
            onChange={({ target }) => setName(target.value)}
          />
          <Input
            type="date"
            variant="range"
            label={FORM.DATE}
            format={DATE_FORMAT}
            placeholders={[FORM.START_DATE, FORM.END_DATE]}
            value={dates}
            onChange={setDates}
          />
        </div>
      </Card>
      <div className="m-top-24" />
      <CollectivePricerEditor
        prices={prices}
        onChange={setPrices}
        axes={axes}
        planId={get(pricer, "insurancePlan.id")}
      />
    </>
  );
};

const DetailCollective = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const navigate = useNavigate();
  const { id } = useParams();
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);

  const { data, loading, error } = useQuery(PRICER, {
    variables: { where: { id } },
    fetchPolicy: "network-only",
  });
  const planId = get(data, "pricer.insurancePlan.id");
  const { data: axesData, loading: axesLoading } = useQuery(
    INSURANCE_PLAN_PRICING_AXES,
    {
      variables: { where: { id: planId } },
      skip: !planId,
      fetchPolicy: "network-only",
    },
  );
  const [updatePricer, { loading: saving }] = useMutation(UPDATE_PRICER, {
    refetchQueries: [{ query: PRICERS, awaitRefetchQueries: true }],
    onCompleted: () =>
      notification.success({ message: t("DETAIL.NOTIFICATION_SUCCESS") }),
    onError: (mutationError) =>
      notification.error({ message: mutationError.message }),
  });

  if (loading || error || axesLoading) return <Loading />;

  return (
    <CollectiveForm
      pricer={data.pricer}
      axes={get(axesData, "insurancePlan")}
      saving={saving}
      cancel={cancel}
      onSave={(values) =>
        updatePricer({ variables: { where: { id }, data: values } })
      }
    />
  );
};

export default DetailCollective;
